import React, { useState, useEffect } from 'react';
import { bugApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Bug, Send, X } from 'lucide-react';
import { toast } from 'sonner';

export default function BugReportButton({ externalOpen = false, onExternalClose }) {
  const [enabled, setEnabled] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps_to_reproduce: '',
    priority: 'medium',
  });

  useEffect(() => {
    checkEnabled();
  }, []);

  // Handle external open trigger from sidebar
  useEffect(() => {
    if (externalOpen && enabled) {
      setShowDialog(true);
    }
  }, [externalOpen, enabled]);

  const handleDialogChange = (open) => {
    setShowDialog(open);
    if (!open && onExternalClose) {
      onExternalClose();
    }
  };

  const checkEnabled = async () => {
    try {
      const response = await bugApi.getSettings();
      setEnabled(response.data.enabled);
    } catch (error) {
      setEnabled(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please describe the issue');
      return;
    }

    setSubmitting(true);
    try {
      await bugApi.submit({
        ...formData,
        page_url: window.location.href,
        browser_info: navigator.userAgent,
      });
      
      toast.success('Bug report submitted! Thank you for your feedback.');
      handleDialogChange(false);
      setFormData({
        title: '',
        description: '',
        steps_to_reproduce: '',
        priority: 'medium',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  if (!enabled) return null;

  return (
    <>
      {/* Floating Button */}
      <Button
        onClick={() => setShowDialog(true)}
        className="fixed bottom-6 right-6 h-12 w-12 rounded-full shadow-lg z-50 bg-red-600 hover:bg-red-700"
        data-testid="bug-report-floating-btn"
      >
        <Bug className="h-5 w-5" />
      </Button>

      {/* Bug Report Dialog */}
      <Dialog open={showDialog} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-600" />
              Report a Bug
            </DialogTitle>
            <DialogDescription>
              Found something wrong? Let us know and we'll fix it.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Summary *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief description of the issue"
                className="rounded-sm mt-1"
                data-testid="bug-title-input"
              />
            </div>

            <div>
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(val) => setFormData({ ...formData, priority: val })}
              >
                <SelectTrigger className="rounded-sm mt-1" data-testid="bug-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-green-500" />
                      Low - Minor issue, workaround available
                    </span>
                  </SelectItem>
                  <SelectItem value="medium">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-yellow-500" />
                      Medium - Affects workflow but not blocking
                    </span>
                  </SelectItem>
                  <SelectItem value="high">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-orange-500" />
                      High - Significantly impacts work
                    </span>
                  </SelectItem>
                  <SelectItem value="critical">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-red-500" />
                      Critical - System unusable / data loss
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What happened? What did you expect to happen?"
                className="rounded-sm mt-1 min-h-[100px]"
                data-testid="bug-description-input"
              />
            </div>

            <div>
              <Label htmlFor="steps">Steps to Reproduce (optional)</Label>
              <Textarea
                id="steps"
                value={formData.steps_to_reproduce}
                onChange={(e) => setFormData({ ...formData, steps_to_reproduce: e.target.value })}
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                className="rounded-sm mt-1 min-h-[80px]"
                data-testid="bug-steps-input"
              />
            </div>

            <div className="text-xs text-muted-foreground">
              We'll automatically capture the current page URL and browser info to help diagnose the issue.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-sm bg-red-600 hover:bg-red-700"
                data-testid="bug-submit-btn"
              >
                {submitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Report
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
